import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney } from '../lib/format.js'
import { uploadReceivePhoto } from '../lib/photoUpload.js'
import { resolveActiveRestaurantId } from '../lib/activeRestaurant.js'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'

// Bottom-sheet form for logging a delivery.
// Mirrors WasteEntryForm so the kitchen manager has muscle memory:
//   1. Pick supplier (typeahead — accepts free-text if not in list)
//   2. Pick item (typeahead from the inventory catalog)
//   3. Enter quantity received
//   4. Enter cost-per-unit (defaults to current item cost — override
//      when the supplier's price is different on this delivery)
//   5. Optional notes
//   6. Submit → writes to receive_logs with snapshotted values
//
// Free-text supplier behavior: if the user types a name that's not
// in the suppliers table, we accept it and store as supplier_name
// only (supplier_id stays null). Future: an "Add new supplier" admin
// flow that promotes free-text names into the suppliers table.
export default function ReceiveEntryForm({ open, onClose, onLogged, items, suppliers }) {
  // Supplier
  const [supplierQuery, setSupplierQuery] = useState('')
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  // Item
  const [itemQuery, setItemQuery] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  // Quantity + cost
  const [quantity, setQuantity] = useState('')
  const [costOverride, setCostOverride] = useState('')
  // Misc
  const [notes, setNotes] = useState('')
  // Optional photo: invoice scan, delivery proof, quality reference.
  // Stored as a File until submit, then uploaded to Storage and the
  // resulting URL written into receive_logs.photo_url.
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null) // object URL for <img>
  const [photoUploading, setPhotoUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Active restaurant scope for the receive row + photo upload path.
  // currentRestaurantId is null for single-unit accounts (resolved lazily
  // at submit via the RLS fallback) AND for a multi-unit operator on the
  // all-units roll-up — in that grouped case we must NOT guess a unit, so
  // we block and prompt instead (see handleSubmit).
  const { currentRestaurantId, isGrouped, loading } = useCurrentUnit()

  const supplierRef = useRef(null)
  const itemRef = useRef(null)
  const qtyRef = useRef(null)

  // Reset on every open so a new receiving starts clean.
  useEffect(() => {
    if (open) {
      setSupplierQuery('')
      setSelectedSupplier(null)
      setItemQuery('')
      setSelectedItem(null)
      setQuantity('')
      setCostOverride('')
      setNotes('')
      setError(null)
      setSubmitting(false)
      setPhotoFile(null)
      // Revoke any prior preview URL so we don't leak object URLs.
      setPhotoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
      setPhotoUploading(false)
      setTimeout(() => supplierRef.current?.focus(), 100)
    }
  }, [open])

  // Cleanup the preview's object URL on unmount or when it changes
  // so we don't leave dangling blob URLs around.
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview)
    }
  }, [photoPreview])

  function handlePhotoChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type?.startsWith('image/')) {
      setError('Please pick an image file.')
      return
    }
    // Soft size warning: 12 MB is well above what any reasonable phone
    // shot will produce after iOS HEIC compression. Prevents accidental
    // RAW or screen-recording uploads.
    if (file.size > 12 * 1024 * 1024) {
      setError('That photo is over 12 MB — try a smaller one.')
      return
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  function clearPhoto() {
    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhotoFile(null)
    setPhotoPreview(null)
  }

  // Keep cost override in sync with selected item's current cost.
  // Manager can still type to override on a per-delivery basis.
  useEffect(() => {
    if (selectedItem && !costOverride) {
      setCostOverride(String(selectedItem.cost_per_unit ?? ''))
    }
  }, [selectedItem]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredSuppliers = useMemo(() => {
    const q = supplierQuery.trim().toLowerCase()
    if (!q) return suppliers.slice(0, 6)
    return suppliers
      .filter((s) => s.name.toLowerCase().includes(q))
      .slice(0, 6)
  }, [supplierQuery, suppliers])

  const filteredItems = useMemo(() => {
    const q = itemQuery.trim().toLowerCase()
    if (!q) return items.slice(0, 8)
    return items
      .filter((i) =>
        i.name.toLowerCase().includes(q) ||
        (i.category || '').toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [itemQuery, items])

  const liveCost = useMemo(() => {
    const q = Number(quantity)
    const c = Number(costOverride || selectedItem?.cost_per_unit || 0)
    if (!isFinite(q) || !isFinite(c)) return 0
    return Math.max(0, q * c)
  }, [quantity, costOverride, selectedItem])

  // Resolve supplier: either picked from the list, or free-text typed.
  const resolvedSupplier = useMemo(() => {
    if (selectedSupplier) {
      return { id: selectedSupplier.id, name: selectedSupplier.name }
    }
    const trimmed = supplierQuery.trim()
    if (trimmed) {
      // Free-text supplier — match against the list one more time
      // (case-insensitive) before treating as new.
      const exact = suppliers.find(
        (s) => s.name.toLowerCase() === trimmed.toLowerCase()
      )
      if (exact) return { id: exact.id, name: exact.name }
      return { id: null, name: trimmed }
    }
    return null
  }, [selectedSupplier, supplierQuery, suppliers])

  const canSubmit =
    resolvedSupplier &&
    selectedItem &&
    Number(quantity) > 0 &&
    Number(costOverride) >= 0 &&
    !submitting

  function pickSupplier(s) {
    setSelectedSupplier(s)
    setSupplierQuery(s.name)
    setTimeout(() => itemRef.current?.focus(), 50)
  }

  function pickItem(it) {
    setSelectedItem(it)
    setItemQuery(it.name)
    setCostOverride(String(it.cost_per_unit ?? ''))
    setTimeout(() => qtyRef.current?.focus(), 50)
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      // Block while the unit context is still loading. Before load, isGrouped
      // can transiently read false and the resolver would guess the first
      // active unit — the exact mis-attribution bug. Prompt a retry; no
      // upload, no insert.
      if (loading) {
        setError('One moment — still loading your units.')
        return
      }

      // Resolve the destination unit ONCE, before any upload or insert,
      // so the delivery row and its photo land on the SAME unit. For a
      // grouped operator on the roll-up this returns null — we must not
      // guess a unit; block and prompt instead.
      const restaurantId = await resolveActiveRestaurantId({ currentRestaurantId, isGrouped })
      if (!restaurantId) {
        setError(
          isGrouped
            ? 'Select a unit before logging a delivery.'
            : 'We could not find your restaurant. Try reopening this screen.'
        )
        return
      }

      const total = Number(liveCost.toFixed(2))
      const cost = Number(Number(costOverride).toFixed(4))

      // Upload the photo first if one was attached. If the upload
      // fails we surface the error and STOP — the manager can decide
      // whether to retry or remove the photo and submit without one.
      // This is friendlier than silently dropping the attachment.
      let photo_url = null
      if (photoFile) {
        setPhotoUploading(true)
        try {
          // uploadReceivePhoto throws a friendly error if this is missing.
          photo_url = await uploadReceivePhoto(photoFile, restaurantId)
        } finally {
          setPhotoUploading(false)
        }
      }

      const payload = {
        // Set restaurant_id EXPLICITLY so the single-tenant BEFORE-INSERT
        // trigger no-ops instead of picking an arbitrary first membership.
        // This is the fix for the receive mis-attribution bug.
        restaurant_id: restaurantId,
        supplier_id: resolvedSupplier.id,
        supplier_name: resolvedSupplier.name,
        item_id: selectedItem.id,
        item_name: selectedItem.name,
        quantity: Number(quantity),
        unit: selectedItem.unit,
        cost_per_unit_at_log: cost,
        total_cost: total,
        notes: notes.trim() ? notes.trim() : null,
        photo_url,
      }
      const { error: insErr } = await supabase.from('receive_logs').insert(payload)
      if (insErr) throw insErr
      onLogged?.({ total })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ReceiveEntryForm] insert failed', err)
      // Deterministic friendly message only — never interpolate err.message.
      // RLS denial (Postgres 42501) means the operator lacks access to the
      // resolved unit, which is worth calling out.
      const isRls = err?.code === '42501' || /row-level security/i.test(err?.message || '')
      setError(
        photoUploading
          ? 'Could not upload the photo. Try again or remove it.'
          : isRls
            ? 'This delivery couldn’t be saved to the selected unit — check that you have access to it.'
            : 'Could not log delivery. Check your connection and try again.'
      )
    } finally {
      // Always reset — same pattern as WasteEntryForm.
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Log delivery"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Log Delivery</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-5 px-4 pb-4">
          {/* Supplier */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="recv-supplier">
              Supplier
            </label>
            <input
              id="recv-supplier"
              ref={supplierRef}
              type="text"
              autoComplete="off"
              value={supplierQuery}
              onChange={(e) => {
                setSupplierQuery(e.target.value)
                if (selectedSupplier && e.target.value !== selectedSupplier.name) {
                  setSelectedSupplier(null)
                }
              }}
              placeholder="Pick or type supplier..."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-lg outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />

            {!selectedSupplier && filteredSuppliers.length > 0 && (
              <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-black/10">
                {filteredSuppliers.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => pickSupplier(s)}
                      className="flex w-full items-center justify-between border-b border-black/5 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-base font-medium">
                          {s.name}
                        </div>
                        {s.contact && (
                          <div className="truncate text-xs text-black/60">
                            {s.contact}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!selectedSupplier && supplierQuery.trim() && filteredSuppliers.length === 0 && (
              <p className="mt-2 text-sm text-black/60">
                We'll log this as a new supplier:{' '}
                <span className="font-medium text-black/80">"{supplierQuery.trim()}"</span>
              </p>
            )}
          </div>

          {/* Item */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="recv-item">
              Item
            </label>
            <input
              id="recv-item"
              ref={itemRef}
              type="text"
              inputMode="search"
              autoComplete="off"
              value={itemQuery}
              onChange={(e) => {
                setItemQuery(e.target.value)
                if (selectedItem && e.target.value !== selectedItem.name) {
                  setSelectedItem(null)
                }
              }}
              placeholder="Search items..."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-lg outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />

            {!selectedItem && filteredItems.length > 0 && (
              <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-black/10">
                {filteredItems.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => pickItem(it)}
                      className="flex w-full items-center justify-between border-b border-black/5 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-base font-medium">{it.name}</div>
                        {it.category && (
                          <div className="truncate text-xs text-black/60">{it.category}</div>
                        )}
                      </div>
                      <div className="ml-3 text-right text-sm tabular-nums text-black/70">
                        {formatMoney(it.cost_per_unit)}/{it.unit}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {selectedItem && (
              <div className="mt-2 flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2 text-sm">
                <span className="font-medium">
                  {selectedItem.name}
                  <span className="ml-2 text-black/60">
                    {formatMoney(selectedItem.cost_per_unit)}/{selectedItem.unit} (current)
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItem(null)
                    setItemQuery('')
                    setCostOverride('')
                    setTimeout(() => itemRef.current?.focus(), 50)
                  }}
                  className="text-blue-600 underline"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          {/* Qty + cost-per-unit override */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="recv-qty">
                Quantity {selectedItem ? `(${selectedItem.unit})` : ''}
              </label>
              <input
                id="recv-qty"
                ref={qtyRef}
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-2xl font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="recv-cost">
                Cost / unit
              </label>
              <input
                id="recv-cost"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={costOverride}
                onChange={(e) => setCostOverride(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-2xl font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
          </div>

          {/* Live total */}
          <div className="flex items-center justify-between rounded-lg bg-black/5 px-3 py-2">
            <span className="text-sm text-black/70">Total received</span>
            <span className="text-xl font-medium tabular-nums">
              {formatMoney(liveCost)}
            </span>
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="recv-notes">
              Notes (optional)
            </label>
            <textarea
              id="recv-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. one short box of brisket — supplier crediting back"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {/* Photo (optional) — invoice scan, delivery proof, etc.
              On phones, capture="environment" opens the back camera
              directly via the file picker; on desktop it falls back
              to a normal file dialog. */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="recv-photo">
              Photo (optional)
            </label>
            {!photoPreview ? (
              <input
                id="recv-photo"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                className="block w-full rounded-lg border border-black/15 px-3 py-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
              />
            ) : (
              <div className="relative overflow-hidden rounded-lg border border-black/10">
                <img
                  src={photoPreview}
                  alt="Selected receipt or delivery"
                  className="block max-h-72 w-full object-contain bg-black/5"
                />
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="absolute right-2 top-2 rounded-md bg-white/90 px-2 py-1 text-xs font-medium text-red-700 shadow hover:bg-white"
                >
                  Remove
                </button>
                {photoUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-sm font-medium text-white">
                    Uploading{'\u2026'}
                  </div>
                )}
              </div>
            )}
            <p className="mt-1 text-xs text-black/55">
              Snap the invoice or pallet for your records. Saved with this
              delivery so you can pull it up later.
            </p>
          </div>

          {/* Error moved to sticky header. */}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-2xl font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Logging…'
              : `Log Delivery${liveCost > 0 ? ` · ${formatMoney(liveCost)}` : ''}`}
          </button>
        </div>
      </form>
    </div>
  )
}
